import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  Check,
  XCircle,
  Clock,
  Video,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const authHeader = () => ({ Authorization: 'Bearer ' + localStorage.getItem('session_token') });

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...options.headers },
    ...options,
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
};

const STATUS_COLORS = {
  Scheduled:   { bg: 'bg-blue-100',    text: 'text-blue-700',    chip: 'bg-blue-500',    dot: 'bg-blue-500' },
  Completed:   { bg: 'bg-green-100',   text: 'text-green-700',   chip: 'bg-green-500',   dot: 'bg-green-500' },
  Cancelled:   { bg: 'bg-red-100',     text: 'text-red-700',     chip: 'bg-red-500',     dot: 'bg-red-500' },
  Rescheduled: { bg: 'bg-yellow-100',  text: 'text-yellow-700',  chip: 'bg-yellow-500',  dot: 'bg-yellow-500' },
  Missed:      { bg: 'bg-gray-100',    text: 'text-gray-600',    chip: 'bg-gray-400',    dot: 'bg-gray-400' },
};

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const fmtTime = (t) => t || '';

// ── Day Cell ──────────────────────────────────────────────────────────────────
const DayCell = ({ day, demos, isToday, isSelected, onSelect }) => {
  if (!day) {
    return <div className="bg-gray-50/50 min-h-[100px] rounded-lg" />;
  }

  const visible = demos.slice(0, 3);
  const extra = demos.length - 3;

  return (
    <div
      onClick={() => onSelect(day)}
      className={`min-h-[100px] rounded-lg p-2 border cursor-pointer transition-all ${
        isSelected
          ? 'border-[#843FEF] bg-purple-50 shadow-md ring-1 ring-[#843FEF]/20'
          : isToday
          ? 'border-blue-300 bg-blue-50/40 hover:border-[#843FEF]/40'
          : 'border-gray-100 bg-white hover:border-[#843FEF]/30 hover:bg-purple-50/20'
      }`}
    >
      <div className={`text-sm font-semibold mb-1.5 w-6 h-6 flex items-center justify-center rounded-full ${
        isToday ? 'bg-[#843FEF] text-white' : isSelected ? 'text-[#843FEF]' : 'text-gray-700'
      }`}>
        {day}
      </div>
      <div className="space-y-0.5">
        {visible.map((d, i) => {
          const sc = STATUS_COLORS[d.status] || STATUS_COLORS.Scheduled;
          const label = d.organization_name || d.organization || d.contact_person || d.contact?.name || 'Demo';
          return (
            <div
              key={d.id || d._id || i}
              className={`text-xs px-1.5 py-0.5 rounded ${sc.bg} ${sc.text} truncate font-medium`}
              title={label}
            >
              {label.length > 14 ? label.slice(0, 13) + '…' : label}
            </div>
          );
        })}
        {extra > 0 && (
          <div className="text-xs text-[#843FEF] font-medium px-1">+{extra} more</div>
        )}
      </div>
    </div>
  );
};

// ── Side Panel ────────────────────────────────────────────────────────────────
const DayPanel = ({ date, demos, onClose, onActionDone }) => {
  const [actioning, setActioning] = useState(null);

  const doAction = async (demo, action) => {
    setActioning(demo.id || demo._id);
    try {
      await apiFetch(`/api/crm/demos/${demo.id || demo._id}/${action}`, {
        method: 'PUT',
        body: JSON.stringify({ status: action === 'complete' ? 'Completed' : 'Cancelled' }),
      });
      onActionDone();
    } catch { /* silent */ }
    finally { setActioning(null); }
  };

  const label = date
    ? `${DAY_NAMES[date.getDay()]}, ${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`
    : '';

  return (
    <div className="w-80 flex-shrink-0 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-purple-50">
        <div>
          <p className="text-xs text-gray-500 font-medium">Selected Day</p>
          <p className="text-sm font-bold text-gray-800">{label}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {demos.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No demos on this day</p>
          </div>
        ) : demos.map((demo) => {
          const sc = STATUS_COLORS[demo.status] || STATUS_COLORS.Scheduled;
          const id = demo.id || demo._id;
          return (
            <div key={id} className={`p-3 rounded-xl border ${sc.bg} border-opacity-50`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {demo.contact_person || demo.contact?.name || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {demo.organization_name || demo.organization || ''}
                  </p>
                </div>
                <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}>
                  {demo.status}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-2">
                <Clock className="w-3 h-3" />
                {fmtTime(demo.demo_time)} · {demo.meeting_platform || demo.platform || ''}
                {demo.duration_minutes ? ` · ${demo.duration_minutes}min` : demo.duration ? ` · ${demo.duration}min` : ''}
              </div>
              {demo.meeting_url && (
                <a
                  href={demo.meeting_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 mb-2 px-2.5 py-1.5 bg-[#843FEF] text-white rounded-lg text-xs font-semibold hover:bg-purple-700 transition-colors w-fit"
                >
                  <Video className="w-3 h-3" />
                  Join Meeting
                </a>
              )}
              {demo.assigned_team_member && (
                <p className="text-xs text-gray-500 mb-2">Assigned: {demo.assigned_team_member}</p>
              )}
              {(demo.status === 'Scheduled' || demo.status === 'Rescheduled') && (
                <div className="flex gap-1.5">
                  <button
                    onClick={() => doAction(demo, 'complete')}
                    disabled={actioning === id}
                    className="flex items-center gap-1 px-2.5 py-1 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 disabled:opacity-50 transition-colors"
                  >
                    <Check className="w-3 h-3" />
                    {actioning === id ? '…' : 'Complete'}
                  </button>
                  <button
                    onClick={() => doAction(demo, 'cancel')}
                    disabled={actioning === id}
                    className="flex items-center gap-1 px-2.5 py-1 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 disabled:opacity-50 transition-colors"
                  >
                    <XCircle className="w-3 h-3" />
                    Cancel
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CRMDemoCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDemos, setDayDemos] = useState([]);
  const [viewMode, setViewMode] = useState('month');

  const fetchDemos = useCallback(async (year, month) => {
    setLoading(true);
    try {
      const data = await apiFetch(`/api/crm/demos/calendar?year=${year}&month=${month}`);
      setDemos(data.demos || data || []);
    } catch {
      setDemos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    fetchDemos(year, month);
  }, [currentDate, fetchDemos]);

  // Build calendar grid
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth(); // 0-based
  const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isThisMonth = today.getFullYear() === year && today.getMonth() === month;

  // Map demos by day
  const demosByDay = {};
  demos.forEach((demo) => {
    const d = demo.demo_date ? new Date(demo.demo_date) : null;
    if (!d) return;
    // Only show demos for current month
    if (d.getFullYear() !== year || d.getMonth() !== month) return;
    const day = d.getDate();
    if (!demosByDay[day]) demosByDay[day] = [];
    demosByDay[day].push(demo);
  });

  // Grid cells: null for empty, number for day
  const cells = [
    ...Array(firstDayOfMonth).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  // Pad to multiple of 7
  while (cells.length % 7 !== 0) cells.push(null);

  const handleSelectDay = (day) => {
    if (!day) return;
    setSelectedDay(day);
    const date = new Date(year, month, day);
    setDayDemos(demosByDay[day] || []);
    // Store selected date for side panel label
    setSelectedDay({ day, date });
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => { setCurrentDate(new Date()); setSelectedDay(null); };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[#843FEF]" />
            Demo Calendar
          </h1>
          <p className="text-sm text-gray-500 mt-1">Visual overview of all scheduled demos</p>
        </div>
        {/* View Toggle */}
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
          {['month', 'week'].map((v) => (
            <button
              key={v}
              onClick={() => setViewMode(v)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${
                viewMode === v ? 'bg-[#843FEF] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {viewMode === 'week' ? (
        <div className="flex items-center justify-center py-24 bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="text-center">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Week view coming soon</p>
            <p className="text-sm text-gray-400 mt-1">Switch to Month view to see all demos</p>
          </div>
        </div>
      ) : (
        <div className={`flex gap-4 ${selectedDay ? 'items-start' : ''}`}>
          {/* Calendar */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Calendar Nav */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <button
                  onClick={prevMonth}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 min-w-[160px] text-center">
                  {MONTH_NAMES[month]} {year}
                </h2>
                <button
                  onClick={nextMonth}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                {loading && <RefreshCw className="w-4 h-4 text-[#843FEF] animate-spin" />}
                <button
                  onClick={goToday}
                  className="px-3 py-1.5 text-sm font-medium text-[#843FEF] border border-[#843FEF]/30 rounded-lg hover:bg-purple-50 transition-colors"
                >
                  Today
                </button>
              </div>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-gray-100">
              {DAY_NAMES.map((d) => (
                <div key={d} className="text-center py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  {d}
                </div>
              ))}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-7 gap-1 p-2">
              {cells.map((day, idx) => (
                <DayCell
                  key={idx}
                  day={day}
                  demos={day ? (demosByDay[day] || []) : []}
                  isToday={isThisMonth && day === today.getDate()}
                  isSelected={selectedDay?.day === day}
                  onSelect={handleSelectDay}
                />
              ))}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
              {Object.entries(STATUS_COLORS).map(([status, sc]) => (
                <div key={status} className="flex items-center gap-1.5">
                  <div className={`w-2.5 h-2.5 rounded-full ${sc.dot}`} />
                  <span className="text-xs text-gray-600">{status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Side Panel */}
          {selectedDay && (
            <DayPanel
              date={selectedDay.date}
              demos={dayDemos}
              onClose={() => setSelectedDay(null)}
              onActionDone={() => {
                const y = currentDate.getFullYear();
                const m = currentDate.getMonth() + 1;
                fetchDemos(y, m);
                setDayDemos([]);
                setSelectedDay(null);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CRMDemoCalendar;
